import { AxiosError } from "axios";
import { useCallback } from "react";
import {
  QueryFunctionContext,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";

import useCurrentEnvironment from "@/hooks/use-environment";
import { useDeepMemo } from "@/hooks/useDeepMemo";
import { queryClient } from "@/queryClient";
import { LoadedEventDefinition } from "@/types/global";
import { makeRequest } from "@/util";
import { ENDPOINTS } from "@/InsightsPage/constants";
import * as insights from "./insights";
import { fetchInsightsOverview } from "./insights";

import * as a11yReportDownload from "@/accessibility/api/getA11yScanReportDownloadLinks";
import * as aiPersonas from "@/ai-personas/types";
import type { Invite } from "@/components/users/types";

import * as adPerformance from "./ad-performance";
import * as aiChat from "./ai-chat";
import * as aiChatApproval from "./ai-chat-approval";
import * as aiChatContext from "./ai-chat-context";
import * as analytics from "./analytics";
import * as costSavings from "./cost-savings";
import * as funnelsApi from "./funnels";
import * as app from "./app";
import * as audiences from "./audiences";
import * as auditLog from "./auditlog";
import * as consentManagement from "./consent-management";
import * as conversionSeed from "./conversionSeed";
import * as events from "./events";
import * as product from "./product";
import * as profiles from "./profiles";
import * as customerJourneys from "./customer-journeys";
import * as shortLinks from "./short-links";
import * as crossDomainTracking from "./cross-domain-tracking";
import * as linkParamInjection from "./link-param-injection";
import * as sessionReplaySettings from "./session-replay-settings";
import * as surveys from "./surveys";
import * as systemStatus from "./systemStatus";
import * as dataConnectors from "./data-connectors";
import type {
  ExportWtmReportingRequest,
  ExportWtmReportingResponse,
  ExportWtmTrackerPagesRequest,
  UnifiedWtmReportQuery,
  UnifiedWtmReportResponse,
  WtmActivityQuery,
  WtmActivityResponse,
  WtmReportingDataResponse,
  WtmReportingQuery,
  WtmReportPreviewRequest,
  WtmReportPreviewResponse,
  WtmReportSchedule,
  WtmTrackerHistoryResponse,
} from "@/trackers/types";

export * from "./customer-journeys";
export * from "./ad-performance";
export * from "./ai-chat";
export * from "./ai-chat-context";
export * from "./analytics";
export * from "./cost-savings";
export * from "./funnels";
export * from "./app";
export * from "./audiences";
export * from "./auditlog";
export * from "./consent-management";
export * from "./events";
export * from "./insights";
export * from "./product";
export * from "./profiles";
export * from "./short-links";
export * from "./splashpages";
export * from "./cross-domain-tracking";
export * from "./link-param-injection";
export * from "./session-replay-settings";
export * from "./surveys";
export * from "./systemStatus";

/*
 * Update these mappings with the appropriate types to get a typed API.
 * The keynames should match the operation names in the backend API.
 * */
export interface ApiTypingsLookup {
  admin_list_destinations: {
    request: app.AdminListDestinationsRequest;
    response: app.AdminListDestinationsResponse;
  };
  get_audience_list: {
    request: audiences.AudienceListRequest;
    response: audiences.AudienceListResponse;
  };
  get_audience_destination_capabilities: {
    request: {};
    response: audiences.GetAudienceDestinationCapabilitiesResponse;
  };
  get_event_count_summary: {
    request: events.EventCountSummaryRequest;
    response: events.EventCountSummaryResponse;
  };
  get_user_activity: {
    request: {};
    response: app.UserActivityData;
  };
  list_active_destinations: {
    request: app.ListActiveDestinationsRequest;
    response: app.ListActiveDestinationsResponse;
  };
  list_all_destinations_for_env: {
    request: {};
    response: app.ListAllDestinationsForEnvResponse;
  };
  rank_destinations_for_customers: {
    request: product.RankDestinationsRequest;
    response: product.RankDestinationsResponse;
  };
  get_insights_overview: {
    request: insights.DateRangeRequest;
    response: insights.InsightsOverviewResponse;
  };
  get_dynamic_events: {
    request: {};
    response: insights.DynamicEventsResponse;
  };
  get_integration_stats_pull_status: {
    request: insights.IntegrationStatsPullRequest;
    response: insights.IntegrationStatsPullResponse;
  };
  get_env_config: {
    request: Record<string, never>;
    response: insights.GetEnvConfigResponse;
  };
  get_event_def: {
    request: { event_def_id: string };
    response: LoadedEventDefinition | null;
  };
  get_recommendation_cards_with_state: {
    request: insights.DateRangeRequest;
    response: insights.RecommendationCardsResponse;
  };
  submit_recommendation_card_feedback: {
    request: insights.SubmitCardFeedbackRequest;
    response: insights.SubmitCardFeedbackResponse;
  };
  get_insights_source_touch_breakdown: {
    request: insights.TouchBreakdownRequest;
    response: insights.TouchBreakdownResponse;
  };
  get_event_defs_enriched: {
    request: {};
    response: insights.FunnelEvents[];
  };
  analytics_funnel_query: {
    request: insights.FunnelQueryRequest;
    response: insights.FunnelStepResult[];
  };
  admin_get_conversion_seed_candidates: {
    request: { envID: string | null };
    response: conversionSeed.EnvSeedCandidates;
  };
  admin_seed_conversion_events: {
    request: { envID: string; eventDefIds: string[] };
    response: conversionSeed.SeedResult;
  };
  analytics_event_query: {
    request: analytics.AnalyticsEventQueryRequest;
    response: analytics.AnalyticsEventQueryResponse;
  };
  analytics_top_conversions: {
    request: analytics.AnalyticsTopConversionsQueryRequest;
    response: analytics.AnalyticsTopConversionsQueryResponse;
  };
  analytics_top_events: {
    request: analytics.AnalyticsTopEventsQueryRequest;
    response: analytics.AnalyticsTopEventsQueryResponse;
  };
  get_consent_management_config_versions: {
    request: consentManagement.GetConfigVersionsRequest;
    response: consentManagement.GetConfigVersionsResponse;
  };
  get_consent_management_configs: {
    request: {};
    response: consentManagement.GetConsentManagementConfigsResponse;
  };
  get_consent_metrics_summary: {
    request: consentManagement.ConsentMetricsSummaryRequest;
    response: consentManagement.ConsentMetricsSummaryResponse;
  };
  get_consent_metrics_breakdown: {
    request: consentManagement.ConsentMetricsBreakdownRequest;
    response: consentManagement.ConsentMetricsBreakdownResponse;
  };
  get_hipaa_allowlist_auditlog_json: {
    request: auditLog.AuditLogQueryParams;
    response: auditLog.AuditLogResponse;
  };
  admin_get_offline_attributions_customer: {
    request: {};
    response: adPerformance.GetOfflineAttributionsCustomerResponse;
  };
  admin_list_ad_performance_files: {
    request: {};
    response: adPerformance.ListAdPerformanceFilesResponse;
  };
  admin_download_ad_performance_file: {
    request: { key: string };
    response: adPerformance.DownloadAdPerformanceFileResponse;
  };
  funnel_query: {
    request: funnelsApi.PageViewFunnelQueryRequest;
    response: funnelsApi.FunnelStepResult[];
  };
  list_funnels: {
    request: Record<string, never>;
    response: funnelsApi.ListFunnelsResponse;
  };
  create_update_funnel: {
    request: funnelsApi.Funnel;
    response: funnelsApi.Funnel;
  };
  delete_funnel: {
    request: { id: string };
    response: string;
  };
  get_surveys: {
    request: surveys.GetSurveysRequest;
    response: surveys.GetSurveysResponse;
  };
  get_survey: {
    request: surveys.GetSurveyRequest;
    response: surveys.GetSurveyResponse;
  };
  get_survey_results: {
    request: surveys.GetSurveyResultsRequest;
    response: surveys.GetSurveyResultsResponse;
  };
  get_survey_timeseries: {
    request: surveys.GetSurveyTimeseriesRequest;
    response: surveys.GetSurveyTimeseriesResponse;
  };
  get_survey_responses: {
    request: surveys.GetSurveyResponsesRequest;
    response: surveys.GetSurveyResponsesResponse;
  };
  get_cross_domain_tracking_settings: {
    request: crossDomainTracking.GetCrossDomainTrackingSettingsRequest;
    response: crossDomainTracking.GetCrossDomainTrackingSettingsResponse;
  };
  update_cross_domain_tracking_settings: {
    request: crossDomainTracking.UpdateCrossDomainTrackingSettingsRequest;
    response: crossDomainTracking.UpdateCrossDomainTrackingSettingsResponse;
  };
  get_link_param_injection_settings: {
    request: linkParamInjection.GetLinkParamInjectionSettingsRequest;
    response: linkParamInjection.GetLinkParamInjectionSettingsResponse;
  };
  update_link_param_injection_settings: {
    request: linkParamInjection.UpdateLinkParamInjectionSettingsRequest;
    response: linkParamInjection.UpdateLinkParamInjectionSettingsResponse;
  };
  get_session_replay_settings: {
    request: sessionReplaySettings.GetSessionReplaySettingsRequest;
    response: sessionReplaySettings.GetSessionReplaySettingsResponse;
  };
  set_session_replay_recording_paused: {
    request: sessionReplaySettings.SetSessionReplayRecordingPausedRequest;
    response: sessionReplaySettings.SetSessionReplayRecordingPausedResponse;
  };
  set_session_replay_sample_rate: {
    request: sessionReplaySettings.SetSessionReplaySampleRateRequest;
    response: sessionReplaySettings.SetSessionReplaySampleRateResponse;
  };
  set_session_replay_mask_inputs: {
    request: sessionReplaySettings.SetSessionReplayMaskInputsRequest;
    response: sessionReplaySettings.SetSessionReplayMaskInputsResponse;
  };
  set_session_replay_exclude_urls: {
    request: sessionReplaySettings.SetSessionReplayExcludeUrlsRequest;
    response: sessionReplaySettings.SetSessionReplayExcludeUrlsResponse;
  };
  surveyPageURLSuggestions: {
    request: surveys.SurveyPageURLSuggestionsRequest;
    response: surveys.SurveyPageURLSuggestionsResponse;
  };
  ai_chat_list_conversations: {
    request: aiChat.ListConversationsRequest;
    response: aiChat.ListConversationsResponse;
  };
  ai_chat_get_conversation: {
    request: aiChat.GetConversationRequest;
    response: aiChat.GetConversationResponse;
  };
  ai_chat_save_conversation: {
    request: aiChat.SaveConversationRequest;
    response: aiChat.SaveConversationResponse;
  };
  ai_chat_get_approval_status: {
    request: aiChatApproval.GetApprovalStatusRequest;
    response: aiChatApproval.GetApprovalStatusResponse;
  };
  ai_personas_list: {
    request: aiPersonas.ListAiPersonasRequest;
    response: aiPersonas.ListAiPersonasResponse;
  };
  ai_personas_save: {
    request: aiPersonas.SaveAiPersonaRequest;
    response: aiPersonas.SaveAiPersonaResponse;
  };
  ai_personas_delete: {
    request: aiPersonas.DeleteAiPersonaRequest;
    response: aiPersonas.DeleteAiPersonaResponse;
  };
  ai_personas_reorder: {
    request: aiPersonas.ReorderAiPersonasRequest;
    response: aiPersonas.ReorderAiPersonasResponse;
  };
  ai_chat_get_context_settings: {
    request: aiChatContext.GetContextSettingsRequest;
    response: aiChatContext.GetContextSettingsResponse;
  };
  ai_chat_set_account_context: {
    request: aiChatContext.SetAccountContextRequest;
    response: aiChatContext.SetAccountContextResponse;
  };
  ai_chat_set_personal_context: {
    request: aiChatContext.SetPersonalContextRequest;
    response: aiChatContext.SetPersonalContextResponse;
  };
  get_system_status_wtm: {
    request: Record<string, never>;
    response: systemStatus.SystemStatusWTMSummaryResponse;
  };
  get_system_status_wtm_history: {
    request: systemStatus.SystemStatusWTMHistoryRequest;
    response: systemStatus.SystemStatusWTMHistoryResponse;
  };
  get_system_status_wtm_export: {
    request: Record<string, never>;
    response: string;
  };
  get_unified_wtm_report: {
    request: UnifiedWtmReportQuery;
    response: UnifiedWtmReportResponse;
  };
  get_wtm_reporting_data: {
    request: WtmReportingQuery;
    response: WtmReportingDataResponse;
  };
  get_wtm_activity: {
    request: WtmActivityQuery;
    response: WtmActivityResponse;
  };
  get_wtm_tracker_history: {
    request: { trackerKey: string; beforeSequenceId?: number };
    response: WtmTrackerHistoryResponse;
  };
  preview_wtm_report: {
    request: WtmReportPreviewRequest;
    response: WtmReportPreviewResponse;
  };
  export_wtm_reporting: {
    request: ExportWtmReportingRequest;
    response: ExportWtmReportingResponse;
  };
  export_wtm_tracker_pages: {
    request: ExportWtmTrackerPagesRequest;
    response: ExportWtmReportingResponse;
  };
  get_wtm_report_schedule: {
    request: Record<string, never>;
    response: WtmReportSchedule | null;
  };
  save_wtm_report_schedule: {
    request: WtmReportSchedule;
    response: WtmReportSchedule;
  };
  get_mtu_by_project: {
    request: Record<string, never>;
    response: {
      months: string[];
      rows: {
        projectID: string;
        projectName: string;
        envID: string;
        envName: string;
        year: number;
        month: number;
        yearMonth: string;
        mtuUsage: number;
      }[];
    };
  };
  search_profiles: {
    request: profiles.SearchProfilesRequest;
    response: profiles.SearchProfilesResponse;
  };
  get_profile: {
    request: profiles.GetProfileRequest;
    response: profiles.GetProfileResponse;
  };
  get_profile_events: {
    request: profiles.GetProfileEventsRequest;
    response: profiles.GetProfileEventsResponse;
  };
  get_profile_session_events: {
    request: profiles.GetProfileSessionEventsRequest;
    response: profiles.GetProfileSessionEventsResponse;
  };
  get_profile_surveys: {
    request: profiles.GetProfileSurveysRequest;
    response: profiles.GetProfileSurveysResponse;
  };
  get_profile_filter_options: {
    request: Record<string, never>;
    response: profiles.ProfileFilterOptionsResponse;
  };
  get_profiles_true_last_seen: {
    request: profiles.GetProfilesTrueLastSeenRequest;
    response: profiles.GetProfilesTrueLastSeenResponse;
  };
  get_spend_savings: {
    request: Record<string, never>;
    response: costSavings.SpendSavingsResponse;
  };
  get_journey_sankey: {
    request: customerJourneys.JourneySankeyRequest;
    response: customerJourneys.JourneySankeyResponse;
  };
  get_top_journeys: {
    request: customerJourneys.TopJourneysRequest;
    response: customerJourneys.TopJourneysResponse;
  };
  get_channel_contribution: {
    request: customerJourneys.ChannelJourneyRequest;
    response: customerJourneys.ChannelContributionResponse;
  };
  get_a11y_crawler_scan_report_download_links: {
    request: a11yReportDownload.A11yScanReportDownloadLinksRequest;
    response: a11yReportDownload.A11yScanReportDownloadLinks;
  };
  get_short_links: {
    request: { urlQuery?: string };
    response: shortLinks.ShortLinkListEntry[];
  };
  get_short_link: {
    request: { shortLinkId: number };
    response: shortLinks.ShortLinkListEntry;
  };
  get_short_link_clicks: {
    request: { shortLinkId: number };
    response: shortLinks.ShortLinkClickRecord[];
  };
  get_short_link_tags: {
    request: Record<string, never>;
    response: shortLinks.ShortLinkTagUsage[];
  };
  create_short_link_tag: {
    request: { name: string };
    response: shortLinks.ShortLinkTag;
  };
  get_short_link_domains: {
    request: Record<string, never>;
    response: shortLinks.ShortLinkDomain[];
  };
  get_short_link_time_series: {
    request: {
      granularity: "hourly" | "daily" | "weekly" | "monthly";
      clientTimezone: string;
      params: shortLinks.ShortLinkQueryParams;
    };
    response: shortLinks.ShortLinkTimeSeriesResult;
  };
  get_short_link_metrics: {
    request: { field: string; params: shortLinks.ShortLinkQueryParams };
    response: shortLinks.ShortLinkMetricResult[];
  };
  preview_short_link_qr: {
    request: { shortLinkId: number; size: number; preview: boolean };
    response: { pngBase64: string };
  };
  get_segment_property_names: {
    request: { query?: string };
    response: string[];
  };
  get_data_connectors: {
    request: {};
    response: dataConnectors.DataConnector[];
  };
  get_data_connector_details: {
    request: { id: string };
    response: dataConnectors.DataConnector;
  };
  set_data_connector_status: {
    request: { id: string; status: dataConnectors.DataConnectorStatus };
    response: dataConnectors.DataConnector;
  };
  // Account-scoped, not environment-scoped: the backend resolves the account
  // from the caller and skips env validation for this Kind.
  list_invited_users: {
    request: {};
    response: Invite[];
  };
}

/**
 * Typed version of useQuery. It's meant to provide a 1-1 mapping between the backend api and the client cache.
 */
export function useApiQuery<OperationName extends keyof ApiTypingsLookup>(
  queryKey: PublicQueryKey<OperationName>,
  options?: Omit<
    UseQueryOptions<
      ApiTypingsLookup[OperationName]["request"],
      AxiosError,
      ApiTypingsLookup[OperationName]["response"],
      PrivateQueryKey<OperationName>
    >,
    "queryKey" | "queryFn"
  >,
) {
  const environment = useCurrentEnvironment();
  const stableQueryKey = useDeepMemo<PrivateQueryKey<OperationName>>([
    ...queryKey,
    environment?.ID || "",
  ]);

  return useQuery(stableQueryKey, makeRequestFetcher, options);
}

/**
 * Fetches the insights overview from the v2 endpoint
 * (GET /v2/internal/insights/overview) instead of the v1 "list" dispatcher.
 *
 * The query key is kept prefixed with ENDPOINTS.GET_INSIGHTS_OVERVIEW and
 * env-scoped, matching useApiQuery's key shape, so existing
 * invalidateQueries([GET_INSIGHTS_OVERVIEW]) calls (e.g. on dynamic-event
 * edits) continue to invalidate it.
 */
export function useInsightsOverview(
  dateRangeRequest: insights.DateRangeRequest,
  options?: Omit<
    UseQueryOptions<
      insights.InsightsOverviewResponse,
      AxiosError,
      insights.InsightsOverviewResponse,
      readonly unknown[]
    >,
    "queryKey" | "queryFn"
  >,
) {
  const environment = useCurrentEnvironment();
  const queryKey = useDeepMemo<readonly unknown[]>([
    ENDPOINTS.GET_INSIGHTS_OVERVIEW,
    dateRangeRequest,
    environment?.ID || "",
  ]);

  return useQuery(
    queryKey,
    () => fetchInsightsOverview(dateRangeRequest.dateRange),
    options,
  );
}

/**
 * Returns a stable callback that prefetches a query into the cache using the
 * same private key format as useApiQuery (operation + optional ID + params + envID).
 * Safe to call in a useEffect — react-query is a no-op if the data is already cached.
 */
export function usePrefetchApiQuery() {
  const environment = useCurrentEnvironment();
  const client = useQueryClient();

  return useCallback(
    <OperationName extends keyof ApiTypingsLookup>(
      queryKey: PublicQueryKey<OperationName>,
    ) => {
      const privateKey = [
        ...queryKey,
        environment?.ID || "",
      ] as PrivateQueryKey<OperationName>;
      void client.prefetchQuery(privateKey, makeRequestFetcher);
    },
    [client, environment?.ID],
  );
}

/**
 * Typed function to invalidate a query cache key. It just ensures that we're talking about the same query keys.
 */
export function invalidateQuery<OperationName extends keyof ApiTypingsLookup>(
  queryKey: Partial<PublicQueryKey<OperationName>>,
) {
  return queryClient.invalidateQueries({ queryKey });
}

/**
 * Typed function to update a query cache key. It just ensures that we're talking about the same query keys.
 */
export function updateQuery<OperationName extends keyof ApiTypingsLookup>(
  queryKey: Partial<PublicQueryKey<OperationName>>,
  payload:
    | ApiTypingsLookup[OperationName]["response"]
    | ((
        existingData?: ApiTypingsLookup[OperationName]["response"] | null,
      ) => ApiTypingsLookup[OperationName]["response"] | null),
) {
  return queryClient.setQueriesData(queryKey, payload);
}

/**
 * Everything below here is implementation details.
 */

type ID = string;

type PublicQueryKey<OperationName extends keyof ApiTypingsLookup> =
  | [OperationName, ApiTypingsLookup[OperationName]["request"]]
  | [OperationName, ID, ApiTypingsLookup[OperationName]["request"]];

/**
 * Append envID so that queries are scoped to the current environment.
 */
type EnvID = string;
type PrivateQueryKey<OperationName extends keyof ApiTypingsLookup> =
  | [OperationName, ApiTypingsLookup[OperationName]["request"], EnvID]
  | [OperationName, ID, ApiTypingsLookup[OperationName]["request"], EnvID];

function unpackQueryKey<OperationName extends keyof ApiTypingsLookup>(
  queryKey: PrivateQueryKey<OperationName>,
): [OperationName, ApiTypingsLookup[OperationName]["request"]] {
  /*
   * We want to allow for specifiers in the query's cache key for situations like IDs or categories
   * which are less ergonomic to invalidate via the params key.
   * We still are only using the kind + params for the request.
   */
  if (queryKey.length === 4) {
    const [kind, _id, params, _envID] = queryKey;
    return [kind, params];
  }

  const [kind, params, _envID] = queryKey;
  return [kind, params];
}

async function makeRequestFetcher<OperationName extends keyof ApiTypingsLookup>(
  ctx: QueryFunctionContext<PrivateQueryKey<OperationName>>,
) {
  const [kind, params] = unpackQueryKey(ctx.queryKey);

  const { data } = await makeRequest<ApiTypingsLookup[OperationName]>(
    kind,
    params,
  );
  return data;
}
